/**
 * IndexedDB database singleton для Block 12 + Block 13 offline-first.
 *
 * Database name: `pomoshnik-offline-v1` (версия в имени для major schema changes).
 * 5 object stores:
 *   - methodologies — cache calculator methodology JSONs (TTL 30 days via fetchedAt)
 *   - projects — calculator project drafts (offline-first, sync через Background Sync)
 *   - quotes — Quote (КП) drafts (linked to projects.id)
 *   - pendingMedia — blob queue для PDF/photo upload (R2 sync через Background Sync)
 *   - contracts — Block 13 договор drafts (transient PII per P-009, см. ADR-0025 §Q3)
 *
 * Indexes:
 *   - projects: by-userId, by-syncStatus
 *   - quotes: by-projectId, by-syncStatus
 *   - pendingMedia: by-relatedEntityId
 *   - contracts: by-userId, by-syncStatus, by-createdAt
 *
 * **Version bump v1 → v2 (Block 13 PR-G Phase 1, ADR-0025 §Q3 + Aibek approve 2026-05-23):**
 * добавлен `contracts` store отдельно от `quotes`. Rationale: разный shape, разный
 * жизненный цикл (статусы черновик/готов/отправлен), разный PII profile (контракт
 * transient — `customerRequisites.value` clearable post-PDF). MVP wipe strategy:
 * deletes all stores, recreates them — приемлемо до production launch (Aibek
 * подтвердил отсутствие ценных сохранённых КП в local IDB; см. BACKLOG B-145
 * «proper IDB migrations» для Phase 9+).
 *
 * Singleton pattern: `openDb()` returns cached Promise — единая connection на app lifecycle.
 *
 * References:
 *   - ADR-0019 §Q11.2 (initial IDB schema, v1)
 *   - ADR-0025 §Q3 (separate contracts store rationale)
 *   - https://github.com/jakearchibald/idb (industry std wrapper)
 */

import { openDB, type IDBPDatabase, type DBSchema } from 'idb';
import type {
  CalculatorMethodology,
  CalculatorProjectPayload,
  ContractDraft,
} from '@pomoshnik/shared';

export const DB_NAME = 'pomoshnik-offline-v1';
/**
 * Phase 6.5F (ADR-0025 Amendment A7, 2026-05-25) — bump v3→v4 для spec schema
 * rewrite: `ContractSpecRowSchema` drop `value`, add `type` + `brand` separate
 * fields; drop 3 material slots (facade_material/corpus_material/hardware) —
 * только block_cost остаётся fixed. Mirror КП CompositionRow structure для
 * 1:1 mapping. Single wipe acceptable per Aibek decision 2026-05-25 (no
 * production users; mirror Phase 1 + 6.5B precedent). См. BACKLOG B-145
 * «Proper IDB schema migrations» — trigger remains production launch.
 *
 * Previous: Phase 6.5B Amendment A4 (2026-05-24) bump v2→v3 для currency
 * user-controlled (A1) + spec rows redesign (A2) + costCustomRows (A3).
 */
export const DB_VERSION = 4;

export type SyncStatus = 'local' | 'pending' | 'synced';

export interface MethodologyRecord {
  type: string; // PK
  structure: CalculatorMethodology;
  fetchedAt: number;
  etag: string;
}

export interface ProjectRecord {
  id: string; // PK (UUID)
  data: CalculatorProjectPayload;
  furnitureType: string;
  name: string;
  total: number;
  syncStatus: SyncStatus;
  createdAt: number;
  updatedAt: number;
  userId: string;
}

export interface QuoteRecord {
  id: string; // PK (UUID)
  projectId: string;
  data: unknown; // QuoteData — будет уточнено в PR-D
  pdfBlobKey: string | null;
  syncStatus: SyncStatus;
  createdAt: number;
  updatedAt: number;
}

export type PendingMediaKind = 'quote-pdf' | 'contract-pdf';

export interface PendingMediaRecord {
  id: string; // PK (UUID)
  blob: Blob;
  kind: PendingMediaKind;
  targetEndpoint: string;
  relatedEntityId: string | null;
  retryCount: number;
  lastAttemptAt: number | null;
  createdAt: number;
}

/**
 * Block 13 PR-G Phase 1 — ADR-0025 §Q3.
 *
 * Запись договора. `data: ContractDraft` содержит transient PII в
 * `customerRequisites[].value` — Phase 7 реализует
 * `clearTransientRequisites(contractId)` post-PDF.
 *
 * `pdfBlobKey: null` всегда в Phase 1 (no R2 backend). Phase 9+ заполнит при
 * upload в R2. `syncStatus: 'local'` всегда в Phase 1.
 */
export interface ContractRecord {
  id: string; // PK (UUID)
  userId: string;
  data: ContractDraft;
  /** PDF R2 key — null до Phase 9+ backend. */
  pdfBlobKey: string | null;
  syncStatus: SyncStatus;
  createdAt: number;
  updatedAt: number;
}

export interface PomoshnikOfflineDBSchema extends DBSchema {
  methodologies: {
    key: string;
    value: MethodologyRecord;
  };
  projects: {
    key: string;
    value: ProjectRecord;
    indexes: {
      'by-userId': string;
      'by-syncStatus': string;
    };
  };
  quotes: {
    key: string;
    value: QuoteRecord;
    indexes: {
      'by-projectId': string;
      'by-syncStatus': string;
    };
  };
  pendingMedia: {
    key: string;
    value: PendingMediaRecord;
    indexes: {
      'by-relatedEntityId': string;
    };
  };
  contracts: {
    key: string;
    value: ContractRecord;
    indexes: {
      'by-userId': string;
      'by-syncStatus': string;
      'by-createdAt': number;
    };
  };
}

let dbPromise: Promise<IDBPDatabase<PomoshnikOfflineDBSchema>> | null = null;

/**
 * Open (or return cached) database connection.
 * Singleton: first call creates Promise, subsequent calls return same Promise.
 *
 * Schema creation on upgrade — recreate all stores (MVP wipe strategy).
 * Phase 9+: add proper migration logic with data preservation (см. BACKLOG B-145).
 */
export function openDb(): Promise<IDBPDatabase<PomoshnikOfflineDBSchema>> {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(
      new Error('IDB_UNAVAILABLE: IndexedDB not supported in this environment'),
    );
  }
  if (!dbPromise) {
    dbPromise = openDB<PomoshnikOfflineDBSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // MVP: wipe + recreate всех stores при version bump.
        // Phase 9+: proper schema migrations с data preservation.
        for (const name of Array.from(db.objectStoreNames)) {
          db.deleteObjectStore(name);
        }

        db.createObjectStore('methodologies', { keyPath: 'type' });

        const projects = db.createObjectStore('projects', { keyPath: 'id' });
        projects.createIndex('by-userId', 'userId');
        projects.createIndex('by-syncStatus', 'syncStatus');

        const quotes = db.createObjectStore('quotes', { keyPath: 'id' });
        quotes.createIndex('by-projectId', 'projectId');
        quotes.createIndex('by-syncStatus', 'syncStatus');

        const pendingMedia = db.createObjectStore('pendingMedia', {
          keyPath: 'id',
        });
        pendingMedia.createIndex('by-relatedEntityId', 'relatedEntityId');

        // Block 13 PR-G Phase 1 — contracts store (ADR-0025 §Q3).
        const contracts = db.createObjectStore('contracts', { keyPath: 'id' });
        contracts.createIndex('by-userId', 'userId');
        contracts.createIndex('by-syncStatus', 'syncStatus');
        contracts.createIndex('by-createdAt', 'createdAt');
      },
      blocked() {
        // Another tab имеет open connection в old version — user должен закрыть tabs.
        console.warn('[idb] Database upgrade blocked — close other tabs');
      },
      blocking() {
        // Этот tab блокирует upgrade — закроем connection чтобы другой tab мог upgrade.
        console.warn('[idb] Database connection blocking upgrade — closing');
        dbPromise?.then((db) => db.close()).catch(() => {});
        dbPromise = null;
      },
      terminated() {
        // Browser убил connection (e.g., low memory) — reset cache.
        dbPromise = null;
      },
    });
  }
  return dbPromise;
}

/** For tests: reset cached connection. */
export function _resetDbCache(): void {
  dbPromise = null;
}
